/**
 * CreateNewOrganizationModal - Modal for creating a new organization while already in an organization
 * Reuses the existing CreateOrganizationForm logic in a modal format
 */
import React, { useState, useCallback, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useOrganization } from '../../context/OrganizationContext';
import { CreateOrganizationInput } from '../../types/organization';

interface CreateNewOrganizationModalProps {
  onClose: () => void;
  onSuccess: (organizationName: string) => void;
}

export const CreateNewOrganizationModal: React.FC<CreateNewOrganizationModalProps> = ({
  onClose,
  onSuccess
}) => {
  const { createOrganization, error, clearError, loadUserOrganizations, switchOrganization } = useOrganization();
  const [formData, setFormData] = useState<CreateOrganizationInput>({
    name: '',
    displayName: ''
  });
  const [slugError, setSlugError] = useState<string>('');
  const [formError, setFormError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  // Generate URL-safe slug from display name
  const generateSlug = (displayName: string): string => {
    return displayName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '') // Remove special characters
      .replace(/\s+/g, '-') // Replace spaces with hyphens
      .replace(/-+/g, '-') // Replace multiple hyphens with single
      .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
  };

  // Validate slug format
  const validateSlug = useCallback((slug: string): string => {
    if (!slug) {
      return 'Organization name is required';
    }

    if (slug.length < 3) {
      return 'Organization name must be at least 3 characters';
    }

    if (slug.length > 50) {
      return 'Organization name must be less than 50 characters';
    }

    if (!/^[a-z0-9-]+$/.test(slug)) {
      return 'Organization name can only contain lowercase letters, numbers, and hyphens';
    }

    if (slug.startsWith('-') || slug.endsWith('-')) {
      return 'Organization name cannot start or end with a hyphen';
    }

    if (slug.includes('--')) {
      return 'Organization name cannot contain consecutive hyphens';
    }

    return '';
  }, []);

  // Update slug when display name changes
  useEffect(() => {
    if (formData.displayName && !formData.name) {
      const newSlug = generateSlug(formData.displayName);
      setFormData(prev => ({ ...prev, name: newSlug }));
    }
  }, [formData.displayName, formData.name]);

  // Validate slug when it changes
  useEffect(() => {
    const error = validateSlug(formData.name);
    setSlugError(error);
  }, [formData.name, validateSlug]);

  // Clear errors when form changes
  useEffect(() => {
    if (error) {
      clearError();
    }
    setFormError('');
  }, [formData, error, clearError]);

  const handleInputChange = (field: keyof CreateOrganizationInput, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSlugChange = (value: string) => {
    // Only allow valid slug characters while typing
    const cleanValue = value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    handleInputChange('name', cleanValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.displayName.trim()) {
      setFormError('Display name is required');
      return;
    }

    if (!formData.name.trim()) {
      setFormError('Organization name is required');
      return;
    }

    if (slugError) {
      setFormError(slugError);
      return;
    }

    setLoading(true);
    setFormError('');

    try {
      const response = await createOrganization({
        name: formData.name.trim(),
        displayName: formData.displayName.trim()
      });

      console.log('Organization created successfully:', response);
      
      // Refresh the organization list
      await loadUserOrganizations();
      
      // Switch to the newly created organization
      await switchOrganization(response.organization.id);
      
      onSuccess(response.organization.displayName);
    } catch (err: any) {
      console.error('Failed to create organization:', err);
      setFormError(err.message || 'Failed to create organization');
    } finally {
      setLoading(false);
    }
  };

  // Handle click outside modal
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const displayError = formError || error;

  const modalContent = (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={handleBackdropClick}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 m-4" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-semibold text-gray-900">Create New Organization</h2>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={loading}
          >
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Display Name Field */}
          <div>
            <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-2">
              Organization Display Name
            </label>
            <input
              id="displayName"
              type="text"
              value={formData.displayName}
              onChange={(e) => handleInputChange('displayName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="e.g., Acme Corporation"
              disabled={loading}
              maxLength={100}
              required
            />
            <p className="text-sm text-gray-500 mt-1">
              The full name of your organization as it will appear in the app.
            </p>
          </div>

          {/* Organization Name (Slug) Field */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
              Organization Name (URL)
            </label>
            <input
              id="name"
              type="text"
              value={formData.name}
              onChange={(e) => handleSlugChange(e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${
                slugError ? 'border-red-300 focus:border-red-500' : 'border-gray-300 focus:border-blue-500'
              }`}
              placeholder="e.g., acme-corp"
              disabled={loading}
              maxLength={50}
              required
            />
            {slugError ? (
              <p className="text-sm text-red-600 mt-1">{slugError}</p>
            ) : (
              <p className="text-sm text-gray-500 mt-1">
                Used in URLs and must be unique. Only lowercase letters, numbers, and hyphens.
              </p>
            )}
          </div>

          {/* Preview */}
          {formData.name && !slugError && (
            <div className="bg-gray-50 p-3 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-1">Preview:</h4>
              <p className="text-sm text-gray-600">
                Organization name: <span className="font-mono font-medium">{formData.name}</span>
              </p>
            </div>
          )}

          {/* Error Display */}
          {displayError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <div className="flex">
                <svg className="w-5 h-5 text-red-400 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-red-700">{displayError}</p>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !formData.displayName.trim() || !formData.name.trim() || !!slugError}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white font-medium rounded-lg transition-colors duration-200 flex items-center"
            >
              {loading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Creating...
                </>
              ) : (
                'Create Organization'
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
  
  // Use React Portal to render modal outside of the main app tree
  const modalRoot = document.getElementById('modal-root');
  if (!modalRoot) {
    return modalContent;
  }
  
  return ReactDOM.createPortal(modalContent, modalRoot);
};

export default CreateNewOrganizationModal;